import { Typography, Grid, CardMedia, Button, IconButton } from '@mui/material';
import { Formik, useField, validateYupSchema, yupToFormErrors } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDocuments, updateApplication } from '../../redux-store/actions/application';
import {
  individuals_Poa_nonIndividuals_Documents,
  FileType,
  Document,
  Bank,
  KraDocumentType,
  ApplicationProps,
} from '../../redux-store/types/api-types';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { RootStateType } from '../../redux-store/reducers';
import { Box } from '@mui/system';
import {
  APPLICATION_TYPE_FOR_DOCUMENTS,
  docInvalidCheck,
  ENABLE_KRA_DOCUMENT_FETCH,
  IMAGE_UPLOAD_FORMATS,
  SAVE_LATER_VALIDATION_CHECK,
} from '../../utils/constant';
import { Notes, ProceedSaveLater } from '../investors/components';
import { useHistory } from 'react-router';
import {
  applicationComparison,
  getFirstErrorField,
  getStep,
  isBankAccountProofUploadedFromBankSection,
  saveForLater,
  scrollToFieldByName,
  shouldValidateUponSaveLater,
  isPhygital,
} from '../../utils/utilityFunctions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { nonIndividualDocumentDetailsSchema } from '../../utils/schema';
import { logout, showError } from '../../redux-store/actions/auth';
import MFSelectField from '../../lib/formik/SelectField';
import { useSnackbar } from 'notistack';
import { KraDocument } from '../investors/kraDocument';
import FieldValidationNote from '../investors/FieldValidationNote';
import { FileUploadDialog } from '../commonComponents';
import en from '../../lang/en-us';
import { getNonIndPdfs } from '../../redux-store/actions';
import { reportDownload } from '../Reports/report-utils';
import LoadingButton from '@mui/lab/LoadingButton';

registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImagePreview,
  FilePondPluginFileValidateSize,
  FilePondPluginFileValidateType
);

type documentDetails = {
  documentType: string;
  documentName: string;
  documentId: string;
  isActive: boolean;
  file?: FileType;
  required?: string;
  uniqueKey?: string;
  options?: string[];
  isDownloadAvailable?: boolean;
  [key: string]: string | boolean | number | FileType | undefined | string[];
};

type documentProps = {
  documents: {
    documentType: string;
    documentName: string;
    documentsList: documentDetails[];
    multipleFiles: string;
    required: string;
    options?: string[];
    isDownloadAvailable?: boolean;
  }[];
  kraDocument?: KraDocumentType[];
};
export interface Values {
  applicants: documentProps[];
  saveType: string;
  banks: Bank[];
  [key: string]: documentProps[] | string | undefined | string[] | Bank[];
}

type DynamicObjectTypeForFileValidation = Record<string, string>;

export const FileUpload = ({
  onFileChange,
  name,
  documentData,
  displayReplaceButton = true,
  errorTextAlign = 'center',
  custodian,
  fileValidateType = {
    'image/jpeg': '.jpeg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'application/pdf': '.pdf',
    // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  },
  supportTitle = 'jpeg, pdf, png, jpg',
}: {
  onFileChange: (
    value: string,
    documentId: string,
    errorOnUpload?: boolean,
    removeFile?: boolean,
    result?: FileType
  ) => void;
  name: string;
  documentData: documentDetails;
  displayReplaceButton: boolean;
  errorTextAlign?: any;
  custodian?: string;
  fileValidateType?: DynamicObjectTypeForFileValidation;
  supportTitle?: string;
}): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [files, setFiles] = useState<any>([]);
  const authToken = useSelector((store: RootStateType) => store.auth.token);
  const { token: investorAuthToken } = useSelector((store: RootStateType) => store.investor);
  const dispatch = useDispatch();
  const [open, setOpen] = useState({ show: false, message: '', code: '' });
  const [hasUploaded, setHasUploaded] = useState(false);

  // eslint-disable-next-line
  const handleOpen = (result: any) => {
    setOpen({ show: true, message: result.message, code: result.code });
  };
  const handleClose = () => {
    setOpen({ show: false, message: '', code: '' });
  };
  useEffect(() => {
    if (files?.length === 0) {
      onFileChange('', documentData?.documentId || '', false);
      setHasUploaded(false);
    }
  }, [files]);
  const handleFileChange = (name: string, documentId: string, result?: FileType) => {
    try {
      onFileChange(name, documentId, false, true, result);
    } catch (e) {
      console.error((e as Error).message);
    }
  };

  return (
    <>
      {!documentData.file ? (
        <Box
          sx={{
            '.filepond--credits': { display: 'none' },
            '.filepond--label-action': {
              textDecoration: 'none',
              fontSize: '14px',
              pointerEvents: 'none',
            },
            '.filepond--drop-label': {
              cursor: 'pointer',
              pointerEvents: 'all',
            },
            '.filepond--file': {
              color: '#000',
              flexWrap: 'wrap',
              fontWeight: 500,
            },
          }}>
          <FilePond
            files={files}
            server={{
              process: {
                url: '/api/files/files',
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${authToken || investorAuthToken}`,
                },
                onload: (response): string => {
                  const result = JSON.parse(response);
                  setHasUploaded(true);
                  result?.name && result?.id && handleFileChange(result.name, result.id, result);
                  return response;
                },
                onerror: (response) => {
                  if (response) {
                    const result = JSON.parse(response);
                    if (result && result.message) {
                      onFileChange('', '', true, true);
                      handleOpen(result);
                    }
                  } else {
                    onFileChange('', '', true, true);
                    handleOpen({ code: '', message: en.networkText.unableToProcess });
                  }
                  return response;
                },
              },
            }}
            onupdatefiles={(fileItems) => {
              setFiles(fileItems);
            }}
            onaddfile={(error, file) => {
              if (error) {
                onFileChange('', '', true, true);
                setHasUploaded(false);
              }
            }}
            onprocessfile={(error, file) => {
              if (error) {
                setHasUploaded(false);
                if (error.code === 401) {
                  dispatch(logout());
                }
              }
            }}
            onprocessfilerevert={() => {
              onFileChange('', '', false, true);
              setHasUploaded(false);
            }}
            name="file"
            maxFileSize={'500kb'}
            allowMultiple={false}
            maxFiles={1}
            acceptedFileTypes={Object.keys(fileValidateType)}
            labelIdle={`<span class="filepond--label-action">Choose File <div>Max Size: 500KB</div>Supports: ${supportTitle} </span>`}
            labelMaxFileSize={'Max file size is 500 KB'}
            labelFileTypeNotAllowed={'Invalid file type'}
            fileValidateTypeLabelExpectedTypesMap={fileValidateType}
            imagePreviewHeight={100}
            allowPaste={false}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px dashed #AAB2BA',
            borderRadius: '6px',
            mb: 2,
            minHeight: '65px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CardMedia
              component="img"
              src={documentData.file.thumbnailUrl || '/images/photo-placeholder.jpg'}
              alt="aadhar"
              width="50"
              height="50"
              sx={{ display: 'flex', width: 'unset', mx: 1 }}
            />
          </Box>
          <Typography
            component="span"
            sx={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
            {documentData.file.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              '.MuiButton-root': { fontWeight: 500, fontSize: 14, p: 0, mr: 1 },
              flexWrap: 'wrap',
            }}>
            <Button
              sx={{
                color: 'primary.main',
                border: '1px dashed #2057A6',
                mb: 1,
              }}
              onClick={() => window.open(documentData.file?.viewUrl)}>
              View
            </Button>
            {displayReplaceButton && (
              <Button
                variant="contained"
                onClick={() => {
                  onFileChange('', '', false, true);
                }}
                sx={{ mb: 1 }}>
                Replace
              </Button>
            )}
            <IconButton
              sx={{
                p: 0,
                pr: 0.5,
                ':hover': {
                  bgcolor: '#F4FCFC',
                },
                cursor: 'pointer',
                pointerEvents: 'all',
                mb: 1,
              }}
              onClick={() => window.open(documentData.file?.url, '_blank')}>
              <CardMedia
                component="img"
                src={'/images/download-icon.png'}
                alt="Download"
                sx={{ width: '25px' }}
              />
            </IconButton>
          </Box>
        </Box>
      )}
      <FileUploadDialog
        handleClose={handleClose}
        show={open.show}
        message={open.message}
        code={open.code}
      />
      {documentData.isDownloadAvailable && (
        <LoadingButton
          disabled={Boolean(documentData?.file) || hasUploaded}
          onClick={async () => {
            const res = await dispatch(getNonIndPdfs(documentData?.documentType, custodian));
            reportDownload(res as unknown as Blob, documentData?.documentType + '.pdf');
            return;
          }}
          style={{
            cursor: 'pointer',
            color: documentData?.file || hasUploaded ? '#9E9E9E' : '#2057A6',
            fontSize: '14px',
            marginTop: '5px',
            textAlign: 'center',
          }}>
          Download a document
        </LoadingButton>
      )}
      {!!errorText && (
        <Typography
          component="span"
          sx={{
            color: '#d32f2f',
            fontSize: '0.75rem',
            display: 'flex',
            justifyContent: errorTextAlign,
          }}>
          {errorText}
        </Typography>
      )}
    </>
  );
};

export const document_object = {
  documentId: '',
  isActive: true,
  errorOnUpload: false,
};
export default function DocumentDetails(): JSX.Element {
  const [documentsData, setDocumentsData] = useState<individuals_Poa_nonIndividuals_Documents[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const custodian = application?.custodian || '';
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const { enqueueSnackbar } = useSnackbar();
  const filePondRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Helper to assign ref to a FilePond field
  const setFilePondRef = (name: string) => (el: HTMLDivElement | null) => {
    filePondRefs.current[name] = el;
  };

  const scrollToFilePondField = (fieldName: string): void => {
    const ref = filePondRefs.current[fieldName];
    if (ref && typeof ref.scrollIntoView === 'function') {
      ref.scrollIntoView({ block: 'center', inline: 'start' });
      ref.focus?.();
    }
  };
  useEffect(() => {
    (async function () {
      try {
        const { applicants = [] } = application || {};
        const getInvestorType = applicants[0]?.investorType?.toString();
        if (getInvestorType) {
          const response = (await dispatch(getDocuments())) as unknown as Document;
          if (
            getInvestorType &&
            Object.keys(response).includes(
              `${getInvestorType}_${(application?.custodian || '').toLowerCase()}`
            )
          ) {
            const baseDocs = (response[
              `${getInvestorType}_${(application?.custodian || '').toLowerCase()}`
            ] || []) as individuals_Poa_nonIndividuals_Documents[];
            const poaDocs = (
              response.individual_poa
                ? (response.individual_poa as individuals_Poa_nonIndividuals_Documents[])
                : []
            ) as individuals_Poa_nonIndividuals_Documents[];
            setDocumentsData(
              [...poaDocs, ...baseDocs].filter((doc) => doc.documentType !== 'poaNotarized')
            );
          }
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, [application]);

  const { applicants = [], banks = [] } = application || {};

  const initialValues: Values = {
    applicants: applicants.map((applicant, applicant_index) => {
      return {
        documents: documentsData
          .filter((document) => {
            // Remove FATCA document for Non-Individual applications
            const removeFromDocs =
              application?.applicationType === APPLICATION_TYPE_FOR_DOCUMENTS.NON_INDIVIDUAL &&
              document.documentType === 'fatca';

            if (
              (applicant.amlCheck && document.documentType === 'compliance_document') ||
              removeFromDocs
            ) {
              return;
            }
            return true;
          })
          .filter((ele) => ele)
          // .map((doc) => ({ ...doc, required: 'true' }))
          // .filter((doc) => {
          //   if (
          //     !applicant.correspondenceAddressEdited &&
          //     doc.documentType === 'correspondenceAddress'
          //   ) {
          //     return;
          //   }
          //   return doc;
          // })
          // .filter((ele) => ele)
          // .map((doc) => {
          //   if (
          //     applicant.correspondenceAddressEdited &&
          //     doc.documentType === 'correspondenceAddress'
          //   ) {
          //     return { ...doc, required: 'true' };
          //   }
          //   return doc;
          // })
          .map((doc, index) => {
            const {
              documentType,
              documentName,
              multipleFiles,
              required,
              options,
              isDownloadAvailable,
            } = doc;
            const { documents: existingDocuments = [] } = applicant || {};
            const docsOfCurrentDocType = existingDocuments
              .filter((doc) => doc.documentType === documentType)
              .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId));
            return {
              documentType: documentType,
              documentName: documentName,
              isDownloadAvailable: isDownloadAvailable,
              documentsList: docsOfCurrentDocType.length
                ? docsOfCurrentDocType.map((doc, ind) => {
                    const {
                      id,
                      documentName = '',
                      documentType = '',
                      documentId = '',
                      isActive = true,
                      file = undefined,
                    } = doc;
                    return {
                      id,
                      applicantId: applicant?.id,
                      documentType,
                      documentName,
                      documentId,
                      isActive,
                      file,
                      options,
                      required,
                      uniqueKey: (applicant_index.toString() +
                        '-' +
                        index.toString() +
                        '-' +
                        ind.toString()) as string,
                      errorOnUpload: document_object.errorOnUpload,
                      isDownloadAvailable,
                    };
                  })
                : [
                    {
                      documentType,
                      documentName,
                      required,
                      options,
                      isDownloadAvailable,
                      uniqueKey: (applicant_index.toString() +
                        '-' +
                        index.toString() +
                        '-' +
                        '0') as string,
                      ...document_object,
                    },
                  ],
              required,
              multipleFiles,
              options,
            };
          }),
        kraDocument: applicant.kraDocument,
      };
    }),
    saveType: 'save and proceed',
    banks: banks,
  };

  const handleSubmit = async (values: Values) => {
    const { applicants, saveType } = values;
    const {
      applicants: exisitingApplicants = [],
      id,
      applicant1ReferenceId = '',
      currentStep,
      applicationNumber,
    } = application || {};
    try {
      const updatedApplicants = exisitingApplicants.map((applicant, index) => {
        const { documents = [] } = applicants[index] || {};
        const finalDocs = documents.map((doc) => {
          if (doc.documentsList.some((_documentCheck) => _documentCheck?.errorOnUpload)) {
            throw docInvalidCheck;
          }
          return doc.documentsList.filter((d) => d.documentId);
        });
        return {
          ...applicant,
          documents: finalDocs.flat().map((doc) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { uniqueKey, required, errorOnUpload, ...rest } = doc;
            return rest;
          }),
        };
      });
      const isSaveLater = saveType !== 'save and proceed';
      const checkApplication = applicationComparison(
        {
          ...application,
          applicants: application?.applicants
            ?.map((applicant) => {
              return {
                ...applicant,
                documents: applicant.documents
                  ?.map((document: any) => {
                    const {
                      documentData,
                      // id,
                      // applicantId,
                      filename,
                      updatedAt,
                      createdAt,
                      updatedBy,
                      createdBy,
                      documentNumber,
                      documentExpiryDate,
                      ...rest
                    } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
        },
        {
          ...application,
          applicants: updatedApplicants
            .map((applicant) => {
              return {
                ...applicant,
                documents: applicant.documents
                  ?.map((document: any) => {
                    const { documentData, options, ...rest } = document;
                    return { ...rest };
                  })
                  .sort((doc1, doc2) => Number(doc1.documentId) - Number(doc2.documentId)),
              };
            })
            .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
          currentStep: getStep(
            !!currentStep && currentStep > 4 ? currentStep : Number(currentStep) + 1,
            isSaveLater
          ),
        }
      );

      if (id && !checkApplication) {
        setLoading(true);
        await dispatch(
          updateApplication({
            body: {
              ...application,
              applicants: updatedApplicants,
              currentStep: getStep(5, isSaveLater),
              ...((!SAVE_LATER_VALIDATION_CHECK as boolean) && { saveForLater: isSaveLater }),
            },
            applicationId: id,
            ...(saveType !== 'save and proceed' && {
              toastMessage: '',
            }),
          })
        );
        saveType === 'save and proceed'
          ? history.push('authorised-signatory', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      } else if (checkApplication) {
        if (saveType !== 'save and proceed') {
          enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        }
        saveType === 'save and proceed'
          ? history.push('authorised-signatory', { id, applicant1ReferenceId })
          : history.push(saveForLater(role, id, applicant1ReferenceId));
      }
    } catch (e) {
      setLoading(false);
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
    }
  };

  const getLabel = (
    documentName: string,
    documentType: string,
    required: string,
    values: Values
  ): string => {
    return `${documentName}  ${
      isPhygital(application as ApplicationProps) && documentType === 'investorSignature'
        ? '*'
        : documentType === 'bankAccountProof'
        ? values.banks.filter((bank) => !bank.pennydropVerified && bank.defaultBankAccount)
            .length !== 0
          ? '*'
          : ''
        : required === 'true'
        ? '*'
        : ''
    } `;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            nonIndividualDocumentDetailsSchema(shouldValidateUponSaveLater(values.saveType)),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}>
      {({ handleSubmit, values, setValues, setFieldValue, errors }) => {
        return (
          <Grid
            container
            rowSpacing={2}
            columnSpacing={5}
            component="form"
            noValidate
            onSubmit={handleSubmit}
            display="flex"
            alignItems="center">
            <Grid sx={{ ml: '40px', width: '100%' }}>
              <Notes
                displayContent={'Please upload latest documents not more than 2 months old'}
                displayContent1={"Please don't upload any password protected files"}
              />
            </Grid>
            {values.applicants.map((applicant, ind) => {
              const { documents } = applicant;
              let num = 0;
              return (
                <>
                  {documents.map((document, doc_index) => {
                    num = doc_index + 1;
                    const {
                      documentType,
                      documentName,
                      documentsList = [],
                      multipleFiles,
                      options,
                      required,
                    } = document;
                    return (
                      <>
                        <React.Fragment key={ind}>
                          <Grid item xs={12} sm={6}>
                            {!options && (
                              <Box display="flex" alignItems="baseline">
                                <Typography
                                  component="span"
                                  sx={{ color: '#2057A6', fontSize: '20px', paddingRight: '15px' }}>
                                  {doc_index + 1}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontSize: 14, fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>
                                  {getLabel(documentName, documentType, required, values)}
                                </Typography>
                              </Box>
                            )}
                            {options && (
                              <Box sx={{ mb: 2 }}>
                                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: '#2057A6',
                                      fontSize: '20px',
                                      paddingRight: '15px',
                                    }}>
                                    {doc_index + 1}
                                  </Typography>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: 'rgba(0,0,0,0.7)',
                                    }}>
                                    {getLabel(documentName, documentType, required, values)}
                                  </Typography>
                                </Box>
                                {documentsList.map((doc, index) => (
                                  <React.Fragment key={index}>
                                    <MFSelectField
                                      name={`applicants.${ind}.documents.${doc_index}.documentsList.${index}.documentName`}
                                      items={options.map((item: string) => ({
                                        key: item,
                                        value: item,
                                      }))}
                                      disabled={isBankAccountProofUploadedFromBankSection(
                                        ind,
                                        documentType,
                                        banks
                                      )}
                                      applyLabelStyles={true}
                                    />
                                  </React.Fragment>
                                ))}
                              </Box>
                            )}
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            display="flex"
                            flexDirection="column"
                            mt={options ? { xs: 1, sm: 5 } : 2}>
                            {documentsList.map((doc, index) => {
                              const hideReplace = isBankAccountProofUploadedFromBankSection(
                                ind,
                                doc.documentType,
                                banks
                              );
                              return (
                                <React.Fragment key={doc.uniqueKey}>
                                  <Box
                                    ref={setFilePondRef(
                                      `applicants.${ind}.documents.${doc_index}.documentsList.${index}.documentId`
                                    )}>
                                    <FileUpload
                                      onFileChange={(
                                        value,
                                        documentId,
                                        errorOnUpload,
                                        removeFile
                                      ) => {
                                        const { file, ...rest } =
                                          values.applicants[ind].documents[doc_index].documentsList[
                                            index
                                          ];
                                        setFieldValue(
                                          `applicants.${ind}.documents.${doc_index}.documentsList.${index}`,
                                          {
                                            ...rest,
                                            documentId,
                                            ...(!removeFile && { file }),
                                            errorOnUpload:
                                              errorOnUpload || document_object.errorOnUpload,
                                          }
                                        );
                                      }}
                                      name={`applicants.${ind}.documents.${doc_index}.documentsList.${index}.documentId`}
                                      documentData={doc}
                                      displayReplaceButton={!hideReplace}
                                      custodian={custodian}
                                    />
                                  </Box>
                                  {index > 0 && !doc.documentId && (
                                    <DeleteOutlineIcon
                                      onClick={() => {
                                        const getDocs = values.applicants[ind].documents[
                                          doc_index
                                        ].documentsList.filter((_, i) => i !== index);
                                        setFieldValue(
                                          `applicants.${ind}.documents.${doc_index}.documentsList`,
                                          getDocs
                                        );
                                      }}
                                      color="error"
                                      sx={{
                                        alignSelf: 'end',
                                        position: 'relative',
                                        bottom: '90px',
                                        left: '22px',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {multipleFiles === 'true' && documentsList.length < 2 && (
                              <Typography
                                component="span"
                                onClick={() => {
                                  const currentDocTypeListLength =
                                    values.applicants[ind].documents[doc_index].documentsList
                                      .length;
                                  const {
                                    required = true,
                                    documentType,
                                    documentName,
                                    documentId,
                                  } = values.applicants[ind].documents[doc_index].documentsList[
                                    currentDocTypeListLength - 1
                                  ];
                                  try {
                                    if (!documentId) {
                                      throw Error(
                                        'Please upload the first document to add more documents'
                                      );
                                    }
                                    setFieldValue(
                                      `applicants.${ind}.documents.${doc_index}.documentsList`,
                                      [
                                        ...values.applicants[ind].documents[doc_index]
                                          .documentsList,
                                        {
                                          ...document_object,
                                          uniqueKey:
                                            ind.toString() +
                                            '-' +
                                            doc_index.toString() +
                                            '-' +
                                            currentDocTypeListLength.toString(),
                                          documentType,
                                          documentName,
                                          required,
                                        },
                                      ]
                                    );
                                  } catch (e) {
                                    dispatch(showError((e as Error).message));
                                  }
                                }}
                                sx={{
                                  color: '#417850',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  textDecoration: 'underline',
                                  alignSelf: 'end',
                                  // position: 'relative',
                                  // bottom:
                                  //   values.applicants[ind].documents[doc_index].documentsList[
                                  //     values.applicants[ind].documents[doc_index].documentsList.length - 1
                                  //   ].documentId !== ''
                                  //     ? '15px'
                                  //     : '50px',

                                  // pointerEvents: applicant.documents[
                                  //   values.applicants[ind].documents.length - 1
                                  // ].documentId
                                  //   ? 'all'
                                  //   : 'none',
                                  cursor: 'pointer',
                                }}>
                                {'Add another document'}
                              </Typography>
                            )}
                          </Grid>
                        </React.Fragment>
                      </>
                    );
                  })}
                  {ENABLE_KRA_DOCUMENT_FETCH && (
                    <KraDocument KraDocument={applicant.kraDocument} num={num + 1} />
                  )}
                </>
              );
            })}
            <FieldValidationNote />
            <ProceedSaveLater
              saveLater={() => {
                setTimeout(() => {
                  const firstErrorField = getFirstErrorField(errors);
                  if (firstErrorField && filePondRefs.current[firstErrorField]) {
                    scrollToFilePondField(firstErrorField);
                  }
                  scrollToFieldByName(firstErrorField || '');
                }, 0);
                setValues({
                  ...values,
                  saveType: 'save for later',
                });
              }}
              saveAndProceed={() => {
                setTimeout(() => {
                  const firstErrorField = getFirstErrorField(errors);
                  if (firstErrorField && filePondRefs.current[firstErrorField]) {
                    scrollToFilePondField(firstErrorField);
                  }
                  scrollToFieldByName(firstErrorField || '');
                }, 0);
                setValues({
                  ...values,
                  saveType: 'save and proceed',
                });
              }}
              loader={loading}
              clickedButton={values.saveType}
            />
          </Grid>
        );
      }}
    </Formik>
  );
}
